import {
  Component,
  OnInit,
  OnDestroy,
  HostBinding,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter
} from "@angular/core";
import { AdvQueueService } from "@onsip/common/services/api/resources/queue/adv-queue.service";
import { QueueWithExt } from "../../../acdQueuesPage/acd-queues-page.component";
import { NonNullableFormBuilder } from "@angular/forms";
import { BehaviorSubject, Subscription } from "rxjs";
import { SnackbarService } from "../../../../../shared/components/snackbar/snackbar.service";
import { TranslateService } from "@ngx-translate/core";
import { take } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { ModalMaterialComponent } from "../../../../../shared/components/modal/modal-material.component";
import {
  ringStrategies,
  ringDurations,
  connectionDelays,
  wrapupTimes,
  ringStrategyMap,
  ringStrategyMapRev
} from "../../../helpers/acd-queues-dropdown-options";

@Component({
  selector: "onsip-acd-queues-agent-edit",
  templateUrl: "./acd-queues-agent-settings-edit.component.html",
  styleUrls: ["./acd-queues-agent-settings-edit.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class AcdQueuesAgentEditComponent implements OnInit, OnDestroy {
  @HostBinding("class.onsip-grid-content")
  @HostBinding("class.onsip-admin-host-grid")
  _dontUse = true;
  @Input() queue!: QueueWithExt;
  @Output() viewPanel = new EventEmitter<void>();

  loadingStateComplete$ = new BehaviorSubject(true);

  ringStrategyMap = ringStrategyMap;
  ringStrategyMapRev = ringStrategyMapRev;
  ringStrategies = ringStrategies;
  ringDurations = ringDurations;
  connectionDelays = connectionDelays;
  wrapupTimes = wrapupTimes;

  queueInfo = this.fb.group({
    ringStrategy: [""],
    ringDuration: [""],
    connectionDelay: [""],
    wrapupTime: [""],
    reportHoldTime: [false]
  });

  private unsubscriber = new Subscription();

  constructor(
    private queueService: AdvQueueService,
    private snackbarService: SnackbarService,
    private translate: TranslateService,
    private dialog: MatDialog,
    private fb: NonNullableFormBuilder
  ) {}

  ngOnInit() {
    this.setFormValues();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  setFormValues(): void {
    this.queueInfo.controls.ringStrategy.setValue(this.ringStrategyMap[this.queue.ringStrategy]);
    this.queueInfo.controls.ringDuration.setValue(this.queue.agentTimeout);
    this.queueInfo.controls.connectionDelay.setValue(this.queue.memberDelay);
    this.queueInfo.controls.wrapupTime.setValue(this.queue.wrapupTime);
    this.queueInfo.controls.reportHoldTime.setValue(this.queue.reportHoldTime);
  }

  togglePanel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(res => {
          if (res && res.doPrimaryAction) this.viewPanel.emit();
        })
    );
  }

  showSaveSnackbar(): void {
    this.snackbarService.openSnackBar(
      this.translate.instant("ONSIP_I18N.YOUR_CHANGES_HAVE_BEEN_SAVED"),
      "success"
    );
  }

  cancel(): void {
    const modal = this.dialog.open(ModalMaterialComponent, {
      panelClass: ["mat-typography", "onsip-dialog-universal-style"],
      data: {
        title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
        message: this.translate.instant("ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"),
        primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
        secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
        primaryBtnFlat: true
      }
    });

    this.unsubscriber.add(
      modal
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj && retObj.doPrimaryAction) {
            this.viewPanel.emit();
          }
        })
    );
  }

  handleSave(): void {
    this.loadingStateComplete$.next(false);
    this.queueService
      .advQueueEdit({
        AdvQueueId: this.queue.advQueueId,
        QueueName: this.queue.queueName,
        MaxCaller: this.queue.maxCaller,
        MaxAgent: this.queue.maxAgent,
        CallerTimeout: this.queue.callerTimeout,
        AgentTimeout: this.queueInfo.controls.ringDuration.value,
        RingStrategy: this.ringStrategyMapRev[this.queueInfo.controls.ringStrategy.value],
        WrapupTime: this.queueInfo.controls.wrapupTime.value,
        MemberDelay: this.queueInfo.controls.connectionDelay.value,
        AnnounceFrequency: this.queue.announceFrequency,
        Username: this.queue.username,
        Password: this.queue.password,
        SmartQueue: this.queue.smartQueue,
        ReportHoldTime: this.queueInfo.controls.reportHoldTime.value,
        EscapeAddress: this.queue.escapeAddress,
        FailoverAddress: this.queue.failoverAddress,
        MusicSourceId: this.queue.musicSourceId,
        StorageServiceId: this.queue.storageServiceId,
        AnnounceRecording: this.queue.announceRecording,
        RecordingTone: this.queue.recordingTone
      })
      .then(result => {
        if (result.status === "success") {
          this.showSaveSnackbar();
          this.viewPanel.emit();
        } else {
          this.snackbarService.openSnackBar(result.data.message, "error");
          this.queueService.clearErrors();
        }
        this.loadingStateComplete$.next(true);
      });
  }
}
